/**
 * Robust clipboard helper.
 * Modern navigator.clipboard.writeText requires a secure context AND, on Safari/iOS,
 * a fresh user gesture. After multiple `await`s the gesture is lost and the call
 * silently rejects. This helper tries the modern API and falls back to the
 * classic textarea + execCommand('copy') approach, which still works in more
 * environments including Safari post-await.
 *
 * Returns true when the text was placed on the clipboard, false otherwise.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  // 1) Modern API — preferred when it works.
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy fallback
  }

  // 2) Legacy fallback via a hidden textarea + execCommand('copy').
  if (typeof document === "undefined") return false;
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
